/**
 * Replay plan for an EnviroLite submission that could not complete online.
 *
 * The online path does five things after `POST /inspections/`: resolve the
 * corrective actions the inspector checked off, create stage observations for
 * dates typed on the form, freeze the saved signature, and generate the
 * report. When the POST is queued for background sync instead, those steps
 * used to be dropped on the floor and the inspection sat on the server as a
 * DRAFT nobody could see (portfolio audit 2026-09-04, C1). The sync service
 * now replays the primary POST and then these follow-ups, substituting the
 * created inspection id wherever `INSPECTION_ID_TOKEN` appears.
 */

export const INSPECTION_ID_TOKEN = '{inspection_id}';

export type LiteFollowUp = {
  method: 'POST' | 'PATCH';
  url: string;
  body?: Record<string, unknown>;
};

export type StageDateEntry = {
  start?: string;
  completion?: string;
  extractedStageId?: string | null;
};

export function buildLiteFollowUps(args: {
  siteId: string;
  verifiedCaIds: Record<string, string>;
  stageDates: Record<string, StageDateEntry>;
  stageNames: string[];
  // JIM-0904-11: a manual-upload inspection attaches the jurisdiction's
  // form instead of generating ours — no signature stamp, no report.
  generateReport?: boolean;
}): LiteFollowUp[] {
  const {
    siteId,
    verifiedCaIds,
    stageDates,
    stageNames,
    generateReport = true,
  } = args;
  const followUps: LiteFollowUp[] = [];

  // Same order as the online path: CA resolution runs after the server's
  // sync_corrective_actions so the inspector's check-off wins.
  for (const [caId, notes] of Object.entries(verifiedCaIds)) {
    followUps.push({
      method: 'PATCH',
      url: `/sites/${siteId}/corrective-actions/${caId}/resolve`,
      body: {
        resolution_notes: notes || null,
        inspection_id: INSPECTION_ID_TOKEN,
      },
    });
  }

  const toDatetime = (d: string | undefined) => (d ? `${d}T00:00:00` : null);
  for (const stageName of stageNames) {
    const entry = stageDates[stageName];
    if (
      !entry ||
      (!entry.start && !entry.completion) ||
      entry.extractedStageId
    ) {
      continue;
    }
    followUps.push({
      method: 'POST',
      url: '/extracted-stages',
      body: {
        inspection_id: INSPECTION_ID_TOKEN,
        site_id: siteId,
        stage_name: stageName,
        canonical_stage: stageName.toLowerCase().replace(/\s+/g, '_'),
        start_date_noted: toDatetime(entry.start),
        completion_date_noted: toDatetime(entry.completion),
        confidence_score: 1.0,
        extraction_method: 'manual',
      },
    });
  }

  if (generateReport) {
    followUps.push({
      method: 'POST',
      url: `/inspections/${INSPECTION_ID_TOKEN}/use-saved-signature`,
    });
    followUps.push({
      method: 'POST',
      url: `/inspections/${INSPECTION_ID_TOKEN}/generate-report`,
    });
  }

  return followUps;
}

/** Deep-replace the inspection-id token in a url or JSON-ish body. */
export function withInspectionId<T>(value: T, inspectionId: string): T {
  if (typeof value === 'string') {
    return value.split(INSPECTION_ID_TOKEN).join(inspectionId) as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => withInspectionId(v, inspectionId)) as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = withInspectionId(v, inspectionId);
    }
    return out as T;
  }
  return value;
}
